import { NeedViewModel } from "../ViewModels";
import { useNeedEnricher } from "./useNeedEnricher";
import {
    ActionStatusHelper,
    DomainDetailHook,
    useAppEnum,
    DOMAIN_CONFIGS,
    DomainHookProps,
    ActionResponse,
    useDetailLifeCycle,
} from "@/Shared";

import { useNeed } from "@/OneLiners";
import { NeedModel } from "../NeedModel";


/**
 * useNeedDetail hook
 *
 * Domain-specific detail hook for Need entities.
 * Acts as the orchestration layer between:
 * - domain data retrieval and enrichment,
 * - permission resolution and access rights,
 * - UI feedback mechanisms (toasts, confirmations),
 * - navigation and redirection logic.
 *
 * Responsibilities:
 * - Initialize the NeedViewModel when data is available.
 * - Handle access rights for UI components.
 * - Provide a unified public API for the UI layer without exposing domain internals.
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 *
 * @see DomainHookProps
 * @see DomainDetailHook<NeedViewModel>
 *
 * @param props DomainHookProps
 * @returns DomainDetailHook<NeedViewModel>
 */
export function useNeedDetail(props: DomainHookProps): DomainDetailHook<NeedViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Need;

    /**
     * Need-related enums exposed to the UI layer
     */
    const { NeedEnums } = useAppEnum();

    /**
     * Ned domain data and lifecycle operations
     */
    const needHook = useNeed();

    /**
     * Domain enrichment layer
     *
     * Responsible for resolving computed fields
     * and cross-domain data for Need entities
     */
    const { enricherResponse, getStrictEnrichedNeed } = useNeedEnricher();

    /**
     * Combined response acting as a single readiness
     * contract for initializing the detail view
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            needHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: needHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Detail life cycle hook
     *
     * Handles:
     * - Entity resolution (queryId / entityId)
     * - ViewModel construction
     * - Post-fetch or post-update handling
     * - Provides public API to the UI layer
     */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: needHook,
        buildVM: (currentEntity: NeedModel, isDisplaying: boolean) => {
            return new NeedViewModel({
                need: getStrictEnrichedNeed(currentEntity),
                viewEnums: NeedEnums,
                accessRight,
                isDisplaying
            })
        },
    });

    /**
     * Public API exposed to the UI layer
     */
    return {
        ...lifeCycleHook
    }
}